import { useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip,
  Link as MuiLink, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import { usePageTitle } from '../../components/shell/AppShell';
import { useRevenueSite } from '../../api/revenue';
import type {
  DataState, MachineRevenueRow, RevenueSite, TrendPoint,
} from '../../api/revenue';
import { fmtCurrencyCents, fmtDate, fmtOrEmDash, fmtPercent } from '../../utils/format';
import { PeriodSelector } from './PeriodSelector';
import type { PeriodValue } from './PeriodSelector';
import { dataStateChip, netWinColor, priorPeriodLabel, revenueDelta } from './revenueFormat';
import { KpiCard } from './KpiCard';
import { renderSuspectDot, SuspectTrendCaption } from './TrendSuspectDot';
import { SuspectDaysDialog } from './SuspectDaysDialog';
import { useRevenueExport } from './useRevenueExport';
import { RevenueRefreshButton } from './RevenueRefreshButton';

/**
 * Site-level revenue drill-down (task 10 — UP-MVP wave 1). Deliberately
 * mirrors `RevenuePortfolioPage`'s structure/markup component-for-component
 * (KPI cards, trend chart, provisional banner, hand-rolled MUI `<Table>` for
 * the machine list) rather than sharing components with it or reaching for
 * `RouteDataGrid` — the revenue pages are a pre-fetched slice of one
 * bundled response with server-side export + routed drill-down, same as
 * the portfolio page. The plain-MUI-Table-vs-RouteDataGrid choice is
 * PARKED for owner decision at final review; until then every revenue page
 * stays consistent with the same pattern.
 *
 * Two spec-mandated gates (kept as inline conditionals, not separate
 * components, so the gating logic stays visible at the call site):
 *   - F-4 / UP-MVP-040 §5.2: the "Machines online" / "Machines stale" KPIs
 *     render ONLY when `dataState.currentPeriod` — connectivity/freshness
 *     are *live* signals and must never sit unlabelled next to a
 *     historical revenue window.
 *   - F-11: the trend chart is hidden for `trend.length <= 1` (Today /
 *     Yesterday are single-gaming-day periods) — never render an
 *     intraday/empty chart.
 *
 * `useRevenueSite` resolves to `null` on a 404 / out-of-scope site (never
 * throws) — rendered as an explicit not-found state, never a crash and
 * never another site's data.
 */
export default function RevenueSitePage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const download = useRevenueExport();

  const [period, setPeriod] = useState<PeriodValue>({ token: 'today' });
  const [reviewOpen, setReviewOpen] = useState(false);
  const q = useRevenueSite(siteId ?? null, period.token, period.from, period.to);
  const site = q.data;
  const notFound = !q.isLoading && !q.isError && site === null;

  usePageTitle(site ? site.siteName : 'Revenue');

  return (
    <Stack spacing={2.5}>
      <Button onClick={() => navigate('/revenue')} sx={{ alignSelf: 'flex-start' }}>
        ← Back to portfolio
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <PeriodSelector value={period} onChange={setPeriod} />
        <Stack direction="row" spacing={1}>
          <RevenueRefreshButton isFetching={q.isFetching} onRefresh={() => { void q.refetch(); }} />
          <Button
            size="small" variant="outlined" disabled={!site}
            onClick={() => download('sites', siteId ?? null, period.token, period.from, period.to, 'csv')}
          >
            Export CSV
          </Button>
          <Button
            size="small" variant="outlined" disabled={!site}
            onClick={() => download('sites', siteId ?? null, period.token, period.from, period.to, 'pdf')}
          >
            Export PDF
          </Button>
        </Stack>
      </Stack>

      {q.isLoading && (
        <Typography variant="body2" color="text.secondary">Loading revenue…</Typography>
      )}
      {q.isError && (
        <Alert severity="error">
          {(q.error as { message?: string })?.message ?? 'Failed to load revenue data.'}
        </Alert>
      )}
      {notFound && (
        <Alert severity="warning">
          Site not found, or you don&apos;t have access to it.
        </Alert>
      )}

      {site && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{site.siteName}</Typography>
          <Typography variant="body2" color="text.secondary">{site.scopeDescription}</Typography>

          {site.dataState.provisional && (
            <ProvisionalBanner
              dataState={site.dataState}
              onReview={() => setReviewOpen(true)}
            />
          )}

          {reviewOpen && (
            <SuspectDaysDialog
              open
              onClose={() => setReviewOpen(false)}
              period={period.token}
              from={period.from}
              to={period.to}
              siteId={site.siteId}
              title={`Suspect meter-days · ${site.siteName}`}
            />
          )}

          <KpiRow site={site} />

          {site.trend.length > 1 && <TrendChart trend={site.trend} />}

          <MachineTable
            rows={site.machines}
            dataState={site.dataState}
            onRowClick={(machineId, versionId) => navigate(`/revenue/machines/${machineId}?versionId=${versionId}`)}
          />
        </>
      )}
    </Stack>
  );
}

function onlineLabel(online: number | null, total: number | null): string {
  return online != null && total != null ? `${online}/${total}` : fmtOrEmDash(null);
}

const TURNOVER_COLOR = '#1976d2';
const NETWIN_COLOR = '#2e7d32';

function KpiRow({ site }: { site: RevenueSite }) {
  const { totals, priorTotals, dataState, machinesOnline, machinesTotal, machinesStale, trend, periodToken } = site;
  const chip = dataStateChip(dataState);
  const priorLabel = priorPeriodLabel(periodToken);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
      <KpiCard
        label="Turnover"
        value={fmtCurrencyCents(totals.turnoverCents)}
        showDelta
        delta={revenueDelta(totals.turnoverCents, priorTotals?.turnoverCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.turnoverCents)}
        sparklineColor={TURNOVER_COLOR}
      />
      <KpiCard
        label="Net win"
        value={fmtCurrencyCents(totals.netWinCents)}
        valueColor={netWinColor(totals.netWinCents)}
        secondary={`Hold ${fmtPercent(totals.holdPct)}`}
        showDelta
        delta={revenueDelta(totals.netWinCents, priorTotals?.netWinCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.netWinCents)}
        sparklineColor={NETWIN_COLOR}
      />
      {/* F-4 / UP-MVP-040 — current-period only. */}
      {dataState.currentPeriod && (
        <KpiCard
          label="Machines online"
          value={onlineLabel(machinesOnline, machinesTotal)}
          hint="Machines with a live device link (ESP32 or SAS), out of all active machines at this site"
        />
      )}
      {dataState.currentPeriod && (
        <KpiCard
          label="Machines stale"
          value={fmtOrEmDash(machinesStale)}
          hint="Machines with no meter data received in the last 5 minutes (or none at all)"
        />
      )}
      <KpiCard label="Data health" value={chip.label} />
    </Box>
  );
}

function TrendChart({ trend }: { trend: TrendPoint[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Turnover vs net win</Typography>
      <Box data-testid="revenue-site-trend-chart" sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="gamingDay" tickFormatter={(v) => fmtDate(v)} minTickGap={30} />
            {/* Net win rides its own right-hand axis — it's only a few % of
                turnover (the hold), so a shared scale flattens it. Axes coloured
                to match their lines. */}
            <YAxis yAxisId="turnover" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#1976d2" />
            <YAxis yAxisId="netwin" orientation="right" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#2e7d32" />
            <RechartsTooltip
              labelFormatter={(v) => fmtDate(v)}
              formatter={(value) => fmtCurrencyCents(Number(value))}
            />
            <Legend />
            {/* dot renders a red ring only on suspect gaming days (included but
                flagged for review); all other points draw nothing. */}
            <Line yAxisId="turnover" type="monotone" dataKey="turnoverCents" name="Turnover" stroke="#1976d2" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
            <Line yAxisId="netwin" type="monotone" dataKey="netWinCents" name="Net win" stroke="#2e7d32" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <SuspectTrendCaption />
    </Paper>
  );
}

function ProvisionalBanner({ dataState, onReview }: {
  dataState: DataState; onReview: () => void;
}) {
  return (
    <Alert severity="warning" variant="outlined">
      <AlertTitle>Provisional data</AlertTitle>
      {dataState.currentPeriod && (
        <Typography variant="body2">
          This period includes today, which is still being collected and may change.
        </Typography>
      )}
      {dataState.affectedCount > 0 && (
        <Box sx={{ mt: dataState.currentPeriod ? 1 : 0 }}>
          <Button variant="text" color="warning" onClick={onReview} sx={{ textTransform: 'none', p: 0, textAlign: 'left' }}>
            {dataState.affectedCount} machine{dataState.affectedCount === 1 ? ' has' : 's have'} suspect
            readings in this period — included in the totals but flagged for review. Review suspect meter-days →
          </Button>
        </Box>
      )}
    </Alert>
  );
}

function MachineTable({ rows, dataState, onRowClick }: {
  rows: MachineRevenueRow[]; dataState: DataState; onRowClick: (machineId: string, versionId: string) => void;
}) {
  const chip = dataStateChip(dataState);
  // Spec §5.3 — default sort Net win desc.
  const sorted = useMemo(() => [...rows].sort((a, b) => b.netWinCents - a.netWinCents), [rows]);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Machines</Typography>
      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No machines in this period.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="machines" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Game</TableCell>
                <TableCell align="right">Net win</TableCell>
                <TableCell align="right">Turnover</TableCell>
                <TableCell align="right">Total out</TableCell>
                <TableCell align="right">Jackpots</TableCell>
                <TableCell align="right">Hold</TableCell>
                <TableCell>Data state</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {sorted.map((r) => (
                <TableRow
                  key={`${r.machineId}:${r.versionId}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onRowClick(r.machineId, r.versionId)}
                >
                  <TableCell>
                    {/* Same owner ruling as the portfolio leaderboards: identify
                        machines by game name; asset number on hover + fallback.
                        One row PER CONFIG (game version) — configs of the same
                        cabinet are separate machines, never summed. */}
                    <MuiLink
                      component={RouterLink}
                      to={`/revenue/machines/${r.machineId}?versionId=${r.versionId}`}
                      onClick={(e) => e.stopPropagation()}
                      title={`${r.machineLabel} — config v${r.versionNo}`}
                    >
                      {r.gameName ?? r.machineLabel} (v{r.versionNo})
                    </MuiLink>
                  </TableCell>
                  <TableCell align="right">
                    <Typography component="span" sx={{ color: netWinColor(r.netWinCents) }}>
                      {fmtCurrencyCents(r.netWinCents)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.turnoverCents)}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.totalOutCents)}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.jackpotsCents)}</TableCell>
                  <TableCell align="right">{fmtPercent(r.holdPct)}</TableCell>
                  <TableCell><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
